import { ReactNode } from 'react'
import { Box } from '@factorialco/deprecated-design-system'

import type { TimeSlot } from 'modules/shift_management/components/ShiftManagement/TimeGridTable/types'

interface Props {
  rowHeaderLabel?: ReactNode
  rowHeaderWidth: number
  minColumnWidth: number
  gridMinWidth: number
  paddingX: number
  paddingY: number
  showGridLines: boolean
  stickyHeaders: boolean
  slots: TimeSlot[]
  timeSlotFormatter: (slot: TimeSlot) => ReactNode
  selectedColumnIndex?: number | null
  onColumnHeaderSelected?: (slotIndex: number) => void
}

const TimeGridHeader = ({
  rowHeaderLabel,
  rowHeaderWidth,
  minColumnWidth,
  gridMinWidth,
  paddingX,
  paddingY,
  showGridLines,
  stickyHeaders,
  slots,
  timeSlotFormatter,
  selectedColumnIndex,
  onColumnHeaderSelected,
}: Props) => {
  return (
    <Box
      role="row"
      data-testid="time-grid-header"
      data-grid-width={gridMinWidth}
      background="grey000White"
      border={
        showGridLines ? { bottom: { width: 's1', style: 'solid', color: 'grey300' } } : undefined
      }
      style={{
        display: 'grid',
        gridTemplateColumns: `${rowHeaderWidth}px repeat(${slots.length}, minmax(${minColumnWidth}px, 1fr))`,
        minWidth: String(gridMinWidth),
        position: stickyHeaders ? 'sticky' : 'relative',
        top: '0px',
        zIndex: stickyHeaders ? '3' : '1',
      }}
    >
      {showGridLines ? (
        <Box
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: '0',
            display: 'grid',
            gridTemplateColumns: `${rowHeaderWidth}px repeat(${slots.length}, minmax(${minColumnWidth}px, 1fr))`,
            pointerEvents: 'none',
          }}
        >
          {/* Header grid overlay keeps day separators aligned with body columns. */}
          <Box
            flexGrow={false}
            border={{ right: { width: 's1', style: 'solid', color: 'grey300' } }}
          />
          {slots.map((slot) => (
            <Box
              key={`header-line-${slot.index}`}
              flexGrow={false}
              border={{ right: { width: 's1', style: 'solid', color: 'grey300' } }}
            />
          ))}
        </Box>
      ) : null}
      <Box
        role="columnheader"
        border={
          showGridLines ? { right: { width: 's1', style: 'solid', color: 'grey300' } } : undefined
        }
        style={{
          padding: `${paddingY}px ${paddingX}px`,
        }}
      >
        {rowHeaderLabel}
      </Box>
      {slots.map((slot) => (
        <Box
          key={slot.index}
          role="columnheader"
          onClick={onColumnHeaderSelected ? () => onColumnHeaderSelected(slot.index) : undefined}
          style={{
            padding: `${paddingY}px ${paddingX}px`,
            cursor: onColumnHeaderSelected ? 'pointer' : undefined,
            userSelect: 'none',
            position: 'relative',
          }}
        >
          {selectedColumnIndex === slot.index ? (
            <Box
              aria-hidden="true"
              style={{
                position: 'absolute',
                inset: '0',
                boxShadow: 'inset 0 0 0 2px #2B74FF',
                pointerEvents: 'none',
              }}
            />
          ) : null}
          {timeSlotFormatter(slot)}
        </Box>
      ))}
    </Box>
  )
}

export default TimeGridHeader
